import { JsonDebugger } from "helper";
import { FC, useEffect, useRef, useState } from "react";
import { DynamicSeo } from "seo";
import { Input, Section } from "ui";
import styles from "./RatioCalculator.module.scss";

interface RatioValues {
	x: {
		pixels: number;
		ratio: number;
	};
	y: {
		pixels: number;
		ratio: number;
	};
}

export const RatioCalculator: FC = () => {
	const previewBoxRef = useRef<HTMLDivElement>(null);
	const [displayValues, setDisplayValues] = useState<number[]>([]);
	const [lockMode, setLockMode] = useState<boolean>(false);
	const [ratioValues, setRatioValues] = useState<RatioValues>({
		x: {
			pixels: 1600,
			ratio: 16,
		},
		y: {
			pixels: 900,
			ratio: 9,
		},
	});

	const gcd = (a: number, b: number) => {
		if (!b) {
			return a;
		}
		return gcd(b, a % b);
	};

	const changeInputValues = (event, direction: "x" | "y", valueType: "ratio" | "pixels") => {
		let _ratioValues = JSON.parse(JSON.stringify(ratioValues));
		let inputValue = parseFloat(event.target.value);
		const validChange = !isNaN(inputValue);
		if (!validChange) {
			inputValue = event.target.value;
		}
		_ratioValues[direction][valueType] = inputValue;
		if (valueType === "pixels" && validChange) {
			_ratioValues.x.ratio = _ratioValues.x.pixels / gcd(_ratioValues.x.pixels, _ratioValues.y.pixels);
			_ratioValues.y.ratio = _ratioValues.y.pixels / gcd(_ratioValues.x.pixels, _ratioValues.y.pixels);
		}
		if (valueType === "ratio" && validChange) {
			const pixelsY = _ratioValues.y.pixels;
			const pixelsX = _ratioValues.x.pixels;
			_ratioValues.x.pixels = pixelsX * (_ratioValues.x.ratio / _ratioValues.y.ratio);
			_ratioValues.y.pixels = pixelsY * (_ratioValues.x.ratio / _ratioValues.y.ratio);
		}
		setRatioValues(_ratioValues);
	};
	useEffect(() => {
		const setDisplayWidths = () => {
			const containerHeight = previewBoxRef.current.clientHeight;
			const containerWidth = previewBoxRef.current.clientWidth;
			const displayValueX =
				ratioValues.x.ratio >= ratioValues.y.ratio
					? containerWidth
					: containerHeight * (ratioValues.x.ratio / ratioValues.y.ratio);
			const displayValueY =
				ratioValues.y.ratio >= ratioValues.x.ratio
					? containerHeight
					: containerHeight * (ratioValues.y.ratio / ratioValues.x.ratio);
			setDisplayValues([displayValueX, displayValueY]);
		};

		window.addEventListener("resize", setDisplayWidths);
		setDisplayWidths();
		// clean up function
		return () => {
			// remove resize listener
			window.removeEventListener("resize", setDisplayWidths);
		};
	}, [ratioValues]);
	return (
		<>
			<DynamicSeo title={"Ratio calculator"} description={"Awesome tool for ratios"} />
			<Section horizontalAlignment={"center"}>
				<div className={styles.previewBox} ref={previewBoxRef}>
					<div
						className={styles.inner}
						style={{
							width: displayValues[0] + "px",
							height: displayValues[1] + "px",
							aspectRatio: ratioValues.x.ratio + " / " + ratioValues.y.ratio,
							background: "red",
						}}
					/>
				</div>
				<Input
					type={"text"}
					name={"ratio-value-x-pixels"}
					value={ratioValues.x.pixels}
					onChange={(e) => changeInputValues(e, "x", "pixels")}
				/>
				X
				<Input
					type={"text"}
					name={"ratio-value-y-pixels"}
					value={ratioValues.y.pixels}
					onChange={(e) => changeInputValues(e, "y", "pixels")}
				/>
				<br />
				<br />
				<input type={"checkbox"} />
				<Input
					type={"text"}
					name={"ratio-value-x-ratio"}
					value={ratioValues.x.ratio}
					onChange={(e) => changeInputValues(e, "x", "ratio")}
				/>
				:
				<Input
					type={"text"}
					name={"ratio-value-y-ratio"}
					value={ratioValues.y.ratio}
					onChange={(e) => changeInputValues(e, "y", "ratio")}
				/>
			</Section>
			<JsonDebugger value={ratioValues} />
		</>
	);
};
